import { ShieldCheck, Sparkles, Users } from 'lucide-react';
import type { ReactNode } from 'react';

import { Dev13Logo } from '@/components/layout/Dev13Logo';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { BRAND_NAME } from '@/config/nav';

const HIGHLIGHTS = [
  {
    icon: Users,
    title: 'Built for your team',
    text: 'A shared space where you and your teammates get work done together.',
  },
  {
    icon: ShieldCheck,
    title: 'Private & secure',
    text: 'Your sign-in is protected, and you only ever see what is meant for you.',
  },
  {
    icon: Sparkles,
    title: 'Everything in one place',
    text: 'Stay organized and keep everyone moving in the same direction.',
  },
];

/**
 * Chrome for the public auth pages (login / forgot / reset): a branded panel on
 * the left (desktop) and the form centered on the right. B2B-styled — strict,
 * dark brand rail with a subtle accent glow + dot grid; collapses to a single
 * column with a compact header on small screens.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  const year = new Date().getFullYear();
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Brand rail — desktop only */}
      <aside className="from-muted to-background text-foreground relative hidden overflow-hidden bg-gradient-to-br px-10 py-12 lg:flex lg:flex-col lg:justify-between lg:border-r">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="bg-primary/20 absolute -top-32 -left-24 size-96 rounded-full blur-3xl" />
          <div className="bg-primary/10 absolute right-0 -bottom-24 size-96 rounded-full blur-3xl" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(100,116,139,0.18)_1px,transparent_0)] [background-size:22px_22px]" />
        </div>

        <div className="relative">
          <Dev13Logo iconClassName="size-10" wordmarkClassName="text-lg" alwaysShowWordmark />
        </div>

        <div className="relative mx-auto w-full max-w-md space-y-10">
          <div className="space-y-3">
            <h1 className="text-3xl leading-tight font-semibold tracking-tight">
              A dedicated workspace for your team.
            </h1>
            <p className="text-muted-foreground">
              Sign in to pick up where you left off and collaborate with your teammates — all in
              one place.
            </p>
          </div>
          <ul className="space-y-5">
            {HIGHLIGHTS.map(({ icon: Icon, title, text }) => (
              <li key={title} className="flex gap-3">
                <span className="bg-foreground/5 ring-foreground/10 mt-0.5 grid size-9 shrink-0 place-items-center rounded-lg ring-1">
                  <Icon className="text-primary size-5" />
                </span>
                <div>
                  <p className="font-medium">{title}</p>
                  <p className="text-muted-foreground text-sm">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-muted-foreground relative text-xs">
          © {year} {BRAND_NAME}. All rights reserved.
        </p>
      </aside>

      {/* Form panel */}
      <div className="flex min-h-screen flex-col lg:min-h-0">
        <header className="flex items-center justify-end p-6">
          <div className="mr-auto lg:hidden">
            <Dev13Logo iconClassName="size-8" alwaysShowWordmark />
          </div>
          <ThemeToggle />
        </header>
        <main className="flex flex-1 items-center justify-center px-4 pb-10">{children}</main>
        <footer className="text-muted-foreground p-6 text-center text-xs lg:hidden">
          © {year} {BRAND_NAME}
        </footer>
      </div>
    </div>
  );
}
